<script setup>
import { computed } from 'vue';
import { DateTime } from 'luxon';
import EmbedMap from "../ui-components/EmbedMap.vue";
import BookingButton from "../../../administration/components/ui-components/widget/BookingButton.vue";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import {usePage} from "@inertiajs/vue3";

const props = defineProps({
    locations: { type: Array, default: () => [] },
    theme: { type: Object, default: () => ({}) },
    currentBrightness: { type: Number, default: 255 },
    roundness: { type: String, default: '' },
    neoDesign: { type: Boolean, default: false },
    priceBreakdown: { type: Object, default: null },
});

function fmtKc(amount) {
    return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', minimumFractionDigits: 0 }).format(amount ?? 0);
}

const page = usePage();

const currentSlide = defineModel('currentSlide');
const form = defineModel('form');
// Volitelné – pokud chceš v rekapu i službu/pracovníka, ponecháme modely připravené
const selectedService = defineModel('selectedService');
const selectedWorker = defineModel('selectedWorker');

const locale = computed(() => 'cs');

// Vybraná lokace podle form.id_reservation_locations
const chosenLocation = computed(() => {
    if (!props.locations?.length || !form?.value) {
        return null;
    }
    return props.locations.find(l => l.id === form.value.id_reservation_locations) ?? null;
});

const containerClasses = computed(() => {
    return [
        props.theme.roundness === 1 ? 'rounded-xl' : '',
        props.theme.roundness === 2 ? 'rounded-2xl' : '',
        props.theme.roundness === 3 ? 'rounded-3xl' : '',
        props.theme.darkMode ? 'bg-transparent border border-neutral-700' : 'bg-white border border-neutral-200',
    ].filter(Boolean).join(' ');
});

// Computed for selected rental item
const selectedItem = computed(() => {
    return usePage().props.rentalItems?.find(item => item.id === form.value?.id_rental_items) ?? null;
});

const headingClasses = computed(() => {
    return [
        props.theme.darkMode ? 'text-white' : 'text-black',
        'flex items-center gap-2 text-lg pb-2'
    ].join(' ');
});

function formatDate(iso) {
    if (!iso) { return '—'; }
    try {
        //console.log(iso);
        const dt = DateTime.fromJSDate(iso);
        if (dt.isValid) {
            return dt.setLocale(locale.value).toFormat('d. LLLL yyyy');
        }
    } catch {
        console.error('Invalid date format:', iso);
    }
    return String(iso);
}

function formatTime(val) {
    if (!val) { return ''; }
    // Akceptujeme HH:mm, HH:mm:ss, případně ISO
    try {
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(val)) {
            return val.substring(0, 5);
        }
        const dt = DateTime.fromISO(val);
        if (dt.isValid) {
            return dt.toFormat('HH:mm');
        }
    } catch {}
    return val;
}

// Rekap – den z form.day (ISO)
const recapDay = computed(() => form?.value?.day ?? null);

// Rekap – timeslot z form.timeslot:
// - "HH:mm:ss/HH:mm:ss" -> HH:mm–HH:mm
// - číslo (ID) -> vypiš jen ID
const recapTimeslot = computed(() => {
    const ts = form?.value?.timeslot;
    if (!ts && ts !== 0) {
        return '—';
    }

    // String s rozsahem "09:00:00/16:00:00"
    if (typeof ts === 'string' && ts.includes('/')) {
        const [start, end] = ts.split('/');
        return `${formatTime(start)}` + (end ? `–${formatTime(end)}` : '');
    }

    // Jednotlivý čas
    if (typeof ts === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(ts)) {
        return formatTime(ts);
    }

    // Číslo -> ID timeslotu – pouze vypsat ID
    if (typeof ts === 'number' || (typeof ts === 'string' && /^\d+$/.test(ts))) {
        return getTimeslotLabelById(ts);
    }

    // Fallback
    return String(ts);
});

function getTimeslotLabelById(id) {
    const timeslot = page.props.forms.valid_days?.map(el => el.time_slots)?.flat()?.find(el => el.id === id)
    // TODO: doplň načtení timeslotu podle ID a vrať např. "09:00–16:00"
    return `${timeslot.time_start?.substring(0,5)} - ${timeslot.time_end?.substring(0,5)}` ?? 'N/A';
}

function normalizeValueForDisplay(value) {
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    if (typeof value === 'boolean') {
        return value ? 'Ano' : 'Ne';
    }
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    return String(value);
}
</script>

<template>
    <!-- ── Neo design ──────────────────────────────────────────────────────── -->
    <div v-if="neoDesign" class="nd-fset">
        <h4>{{ $t('widget.recap') ?? 'Rekapitulace' }}</h4>

        <!-- Summary info card -->
        <div class="nd-summary-card">
            <!-- Rental item name -->
            <div v-if="selectedItem" class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.item_short') }}</span>
                <span class="nd-sm-val">{{ selectedItem.name }}</span>
            </div>
            <!-- Dates -->
            <div v-if="form.day_start && form.day_end" class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.term') }}</span>
                <span class="nd-sm-val">{{ form.day_start }} → {{ form.day_end }}</span>
            </div>
            <div v-else-if="recapDay" class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.date') ?? 'Datum' }}</span>
                <span class="nd-sm-val">{{ formatDate(recapDay) }}</span>
            </div>
            <!-- Guests (accommodation) -->
            <div v-if="form.adults != null && $page.props.forms?.form_type?.name === 'accommodation'" class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.guests_label') }}</span>
                <span class="nd-sm-val">
                    {{ form.adults }} {{ $t('widget.adults_abbr') }}
                    <template v-if="form.children"> · {{ form.children }} {{ $t('widget.children_abbr') }}</template>
                    <template v-if="form.pets"> · {{ form.pets }} {{ $t('widget.pets_abbr') }}</template>
                </span>
            </div>
            <!-- Contact -->
            <div class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.name_label') }}</span>
                <span class="nd-sm-val">{{ form?.name || '—' }}</span>
            </div>
            <div class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.email_label') }}</span>
                <span class="nd-sm-val nd-sm-mono">{{ form?.email || '—' }}</span>
            </div>
            <div v-if="form?.phone" class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.phone_label') }}</span>
                <span class="nd-sm-val">{{ form.phone }}</span>
            </div>
            <div v-if="form?.note" class="nd-sm-row nd-sm-row-col">
                <span class="nd-sm-lbl">{{ $t('widget.note_label') }}</span>
                <span class="nd-sm-val nd-sm-note">{{ form.note }}</span>
            </div>
            <!-- Location -->
            <div v-if="chosenLocation" class="nd-sm-row">
                <span class="nd-sm-lbl">{{ $t('widget.location') }}</span>
                <span class="nd-sm-val">{{ chosenLocation.name }}</span>
            </div>
            <!-- Dynamic formBuilder fields -->
            <template v-if="$page?.props?.formBuilder?.length">
                <div class="nd-sm-divider"/>
                <div v-for="input in $page.props.formBuilder" :key="input.id" class="nd-sm-row">
                    <span class="nd-sm-lbl">{{ input.label }}</span>
                    <span class="nd-sm-val">{{ normalizeValueForDisplay(form?.formdata?.[input.id]) }}</span>
                </div>
            </template>
        </div>

        <!-- Price breakdown -->
        <div v-if="priceBreakdown && priceBreakdown.nights > 0" class="nd-price-breakdown">
            <div class="nd-pb-row">
                <span>{{ priceBreakdown.nights }} × {{ fmtKc(priceBreakdown.base) }}</span>
                <span>{{ fmtKc(priceBreakdown.baseSubtotal) }}</span>
            </div>
            <div v-if="priceBreakdown.hasPopRule" class="nd-pb-row nd-pb-row--adj">
                <span>{{ $t('widget.price_guest_adjustment', { count: priceBreakdown.guestCount }) }}</span>
                <span>{{ priceBreakdown.popRuleAmount > 0 ? '+' : '−' }}{{ fmtKc(Math.abs(priceBreakdown.popRuleAmount)) }}</span>
            </div>
            <div v-if="priceBreakdown.discountPct > 0" class="nd-pb-row nd-pb-row--discount">
                <span>{{ $t('widget.discount_pct', { pct: priceBreakdown.discountPct }) }}</span>
                <span>−{{ fmtKc(priceBreakdown.discountAmount) }}</span>
            </div>
            <div v-for="adj in priceBreakdown.occupancyAdjustments" :key="adj.rule_id" class="nd-pb-row nd-pb-row--adj">
                <span>{{ $t('widget.high_demand_pct', { pct: adj.current_occupancy }) }}</span>
                <span>+{{ fmtKc(adj.applied_adjustment) }}</span>
            </div>
            <div v-if="priceBreakdown.additionalTotal > 0" class="nd-pb-row">
                <span>{{ $t('widget.additional_costs') }}</span>
                <span>+{{ fmtKc(priceBreakdown.additionalTotal) }}</span>
            </div>
            <div class="nd-pb-divider"/>
            <div class="nd-pb-row nd-pb-total">
                <span>{{ $t('widget.bill_total') }}</span>
                <span>{{ fmtKc(priceBreakdown.total) }}</span>
            </div>
        </div>

        <!-- GDPR -->
        <label class="nd-gdpr">
            <input type="checkbox" v-model="form.gdpr_consent"/>
            <span>
                {{ $t('widget.gdpr_consent') }}
                <a href="https://roori.app/gdpr" target="_blank" rel="noopener" class="nd-gdpr-link">
                    {{ $t('widget.gdpr_link_text') }}
                </a>
            </span>
        </label>
        <small v-if="form?.errors?.gdpr_consent" class="nd-err">{{ form.errors.gdpr_consent }}</small>

        <!-- Buttons -->
        <div class="nd-btn-row">
            <button class="nd-btn nd-btn-secondary" @click="currentSlide--">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
                    <path d="M19 12H5M11 6l-6 6 6 6"/>
                </svg>
                {{ $t('common.back') }}
            </button>
            <button
                class="nd-btn nd-btn-primary"
                :disabled="!form.gdpr_consent || form.processing"
                @click="form.submit()"
            >
                <span v-if="form.processing" class="nd-spinner"/>
                <template v-else>{{ $t('widget.send_request') }}</template>
            </button>
        </div>
    </div>

    <!-- ── Original design ─────────────────────────────────────────────────── -->
    <section v-else class="px-5 mb-6">
        <div :class="headingClasses">
            <i class="pi pi-list-check text-gray-400" style="font-size: 1.25rem"></i>
            {{ $t('widget.recap') ?? 'Rekapitulace' }}
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <!-- Rental Item Card -->
            <div v-if="form.id_rental_items && selectedItem" :class="containerClasses" class="p-4 col-span-2">
                <div class="flex gap-4">
                    <img v-if="selectedItem.gallery?.length"
                         :src="selectedItem.gallery[0]?.full_url"
                         class="rounded-lg object-cover h-[110px] w-[110px] shrink-0 hidden sm:block"
                         alt=""/>
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                            <i v-if="selectedItem.type" :class="selectedItem.type" class="text-lg text-primary-500 shrink-0"/>
                            <h3 class="text-lg font-bold text-gray-900 dark:text-white truncate">{{ selectedItem.name }}</h3>
                        </div>
                        <div v-if="selectedItem.description"
                             class="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2"
                             v-html="selectedItem.description"/>
                        <div class="flex items-baseline gap-2 mb-2">
                            <span class="text-xs text-gray-500 dark:text-gray-400">{{ $t('widget.price') }}</span>
                            <span class="text-xl font-bold text-primary-600 dark:text-primary-400">
                                {{ new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: selectedItem.prices?.[0]?.currency ?? 'CZK', minimumFractionDigits: 0 }).format(selectedItem.prices?.[0]?.unit_price ?? 0) }}
                            </span>
                        </div>
                        <div class="flex flex-wrap gap-1.5">
                            <span v-for="attr in selectedItem.attributes?.filter(a => a.is_visible === 1)" :key="attr.id"
                                  class="inline-flex items-center gap-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-200 px-2 py-1 rounded-full">
                                <i v-if="attr.icon" :class="attr.icon" class="text-[10px] text-primary-500"/>
                                {{ attr.label }}: <span class="font-semibold">{{ normalizeValueForDisplay(attr.value) }}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Kontaktní údaje -->
            <div :class="containerClasses" class="p-4">
                <div class="flex items-start gap-3">
                    <i class="pi pi-user text-gray-500 dark:text-gray-300 mt-1"></i>
                    <div class="grow">
                        <p class="text-sm font-semibold text-gray-700 dark:text-gray-100">
                            {{ $t('widget.contact_details') ?? 'Kontaktní údaje' }}
                        </p>
                        <div class="grid grid-cols-2 gap-x-6  gap-y-2 mt-1">
                            <div class="text-sm">
                                <p class="text-gray-500 dark:text-gray-300">{{ $t('widget.name_label') }}</p>
                                <p class="font-medium text-gray-900 dark:text-white break-words [overflow-wrap:anywhere]">{{ form?.name ?? '—' }}</p>
                            </div>
                            <div class="text-sm text-wrap">
                                <p class="text-gray-500 dark:text-gray-300">{{ $t('widget.email_label') }}</p>
                                <p class="font-medium text-gray-900 dark:text-white break-words [overflow-wrap:anywhere]">{{ form?.email ?? '—' }}</p>
                            </div>
                            <div class="text-sm">
                                <p class="text-gray-500 dark:text-gray-300">{{ $t('widget.phone_label') }}</p>
                                <p class="font-medium text-gray-900 dark:text-white">{{ form?.phone !== '' ? form?.phone : '—' }}</p>
                            </div>
                            <div class="text-sm col-span-2">
                                <p class="text-gray-500 dark:text-gray-300">{{ $t('widget.note_label') }}</p>
                                <p class="font-medium text-gray-900 dark:text-white whitespace-pre-line break-words [overflow-wrap:anywhere]">
                                    {{ form?.note !== '' ? form?.note : '—' }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Lokace -->
            <div v-if="!form.id_rental_items" :class="containerClasses" class="p-0">
                <EmbedMap
                    v-if="chosenLocation?.type === 'in_person' && chosenLocation?.latitude && chosenLocation?.longitude"
                    :coordinates="{ lat: chosenLocation.latitude, lng: chosenLocation.longitude }"
                    class="border-b dark:border-neutral-700 rounded-t-inherit"
                    :color="theme.color"
                    :height="140"
                />
                <div class="p-4">
                    <div class="flex items-start gap-3">
                        <i class="pi pi-map-marker text-gray-500 dark:text-gray-300 mt-1"></i>
                        <div class="grow">
                            <p class="text-sm font-semibold text-gray-700 dark:text-gray-100">
                                {{ $t('widget.location') }}
                            </p>
                            <p class="text-base font-medium text-gray-900 dark:text-white">
                                {{ chosenLocation?.name ?? '—' }}
                            </p>
                            <p v-if="chosenLocation?.address" class="text-sm text-gray-600 dark:text-gray-300">
                                {{ JSON.parse(chosenLocation.address)?.display_name }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Služba a pracovník (volitelné, pokud jsou k dispozici) -->
            <div v-if="selectedService" :class="containerClasses" class="p-4">
                <div class="flex items-start gap-3">
                    <i class="pi pi-briefcase text-gray-500 dark:text-gray-300 mt-1"></i>
                    <div class="grow">
                        <p class="text-sm font-semibold text-gray-700 dark:text-gray-100">
                            {{ $t('widget.selected_service') }}
                        </p>
                        <p class="text-base font-medium text-gray-900 dark:text-white">
                            {{ selectedService?.name ?? '—' }}
                            <span v-if="selectedWorker" class="text-gray-500 dark:text-gray-300">
                                – {{ selectedWorker?.name }}
                              </span>
                        </p>
                    </div>
                </div>
            </div>
            <!-- Datum -->
            <div :class="containerClasses" class="flex flex-col">
                <div class="flex items-start gap-3 p-4 grow">
                    <i class="pi pi-calendar text-gray-500 dark:text-gray-300 mt-1"></i>
                    <div class="grow">
                        <p class="text-sm font-semibold text-gray-700 dark:text-gray-100">
                            {{ $t('widget.date') ?? 'Datum' }}
                        </p>
                        <template v-if="form.day_start && form.day_end">
                            <p class="text-base font-medium text-gray-900 dark:text-white">
                                {{ form.day_start }} – {{ form.day_end }}
                            </p>
                        </template>
                        <template v-else>
                            <p class="text-base font-medium text-gray-900 dark:text-white">
                                {{ formatDate(recapDay) }}
                            </p>
                        </template>
                    </div>
                </div>
                <div v-if="!form.id_rental_items" class="flex items-start gap-3 border-t dark:border-neutral-700 p-4 grow">
                    <i class="pi pi-clock text-gray-500 dark:text-gray-300 mt-1"></i>
                    <div class="grow">
                        <p class="text-sm font-semibold text-gray-700 dark:text-gray-100">
                            {{ $t('widget.time') ?? 'Čas' }}
                        </p>
                        <p class="text-base font-medium text-gray-900 dark:text-white">
                            {{ recapTimeslot }}
                        </p>
                    </div>
                </div>
            </div>
            <!-- Dynamická pole (formBuilder) -->
            <div v-if="$page?.props?.formBuilder?.length" :class="containerClasses" class="p-4 lg:col-span-2">
                <div class="flex items-start gap-3">
                    <i class="pi pi-list text-gray-500 dark:text-gray-300 mt-1"></i>
                    <div class="grow">
                        <p class="text-sm font-semibold text-gray-700 dark:text-gray-100">
                            {{ $t('widget.additional_information') ?? 'Další informace' }}
                        </p>
                        <div class="grid md:grid-cols-2 gap-x-6 gap-y-2 mt-2">
                            <div
                                v-for="input in $page.props.formBuilder"
                                :key="input.id"
                                class="text-sm"
                            >
                                <p class="text-gray-500 dark:text-gray-300">{{ input.label }}</p>
                                <p class="font-medium text-gray-900 dark:text-white break-words [overflow-wrap:anywhere]">
                                    {{ normalizeValueForDisplay(form?.formdata?.[input.id]) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <div>
            <div :class="containerClasses" class="p-4 lg:col-span-2 mt-8">
                <div class="flex items-start gap-3">
                    <Checkbox v-model="form.gdpr_consent" binary :aria-label="$t('widget.gdpr_consent')" />
                    <label class="text-sm leading-5 text-gray-800 dark:text-gray-200 cursor-pointer" @click="form.gdpr_consent = !form.gdpr_consent">
                        {{ $t('widget.gdpr_consent') }}
                        <a href="https://roori.app/gdpr" target="_blank" rel="noopener" :class="`text-${theme.color}-600 underline ml-1`">
                            {{ $t('widget.gdpr_link_text') }}
                            <i class="pi pi-arrow-up-right" style="font-size: 0.75rem"></i>
                        </a>
                    </label>
                </div>
                <small v-if="form?.errors?.gdpr_consent" class="text-red-500 mt-1 block">{{ form.errors.gdpr_consent }}</small>
            </div>

            <div class="flex items-center justify-center gap-4 mt-4">

                <Button
                    :class="[`!w-[45px] !border-neutral-300 dark:!border-neutral-700 !text-neutral-500 !h-[45px]`, roundness]"
                    icon="pi pi-arrow-left"
                    outlined
                    severity="contrast"
                    @click="currentSlide--"></Button>
                <booking-button :theme="theme"
                                :loading="form.processing"
                                @click="form.submit()"
                                :disabled="!form.gdpr_consent"
                                :roundness="roundness"
                ></booking-button>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* ── Neo design ──────────────────────────────────────────────────────────── */
.nd-fset {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 20px 16px;
    font-family: var(--rw-font-display, "Bricolage Grotesque", ui-sans-serif, sans-serif);
}

.nd-fset h4 {
    font-family: var(--rw-font-display, "Bricolage Grotesque", ui-sans-serif, sans-serif);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0;
    color: var(--rw-ink, #0F1115);
}

.nd-summary-card {
    border: 1px solid var(--rw-line, #E5E2DC);
    border-radius: 14px;
    padding: 4px 16px;
    background: var(--rw-bg-2, #FBFAF7);
    display: flex;
    flex-direction: column;
}

.nd-sm-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 10px 0;
    font-size: 14px;
    border-bottom: 1px solid var(--rw-line-2, #EDEAE3);
}

.nd-sm-row:last-child {
    border-bottom: none;
}

.nd-sm-row-col {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
}

.nd-sm-lbl {
    font-size: 12px;
    font-weight: 500;
    color: var(--rw-ink-3, #6B7280);
    white-space: nowrap;
    flex-shrink: 0;
}

.nd-sm-val {
    font-weight: 500;
    color: var(--rw-ink, #0F1115);
    text-align: right;
}

.nd-sm-mono {
    font-size: 12px;
}

.nd-sm-note {
    text-align: left;
    font-weight: 400;
    color: var(--rw-ink-2, #3A3F47);
    font-size: 13px;
    white-space: pre-line;
}

.nd-sm-divider {
    height: 1px;
    background: var(--rw-line, #E5E2DC);
    margin: 2px 0;
}

.nd-gdpr {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    background: var(--rw-bg-2, #FBFAF7);
    border-radius: 12px;
    border: 1px solid var(--rw-line, #E5E2DC);
    cursor: default;
}

.nd-gdpr input[type="checkbox"] {
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: var(--brand, #2A6FDB);
    width: 16px;
    height: 16px;
}

.nd-gdpr span {
    font-size: 13px;
    color: var(--rw-ink-2, #3A3F47);
    line-height: 1.5;
}

.nd-gdpr-link {
    color: var(--brand, #2A6FDB);
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.nd-err {
    color: #ef4444;
    font-size: 12px;
}

.nd-btn-row {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding-top: 4px;
}

.nd-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 18px;
    height: 46px;
    border-radius: var(--rw-r-md, 12px);
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, opacity 0.12s;
    font-family: inherit;
    border: none;
}

.nd-btn-primary {
    background: var(--brand, #2A6FDB);
    color: #fff;
    flex: 1;
}

.nd-btn-primary:hover:not(:disabled) {
    background: var(--rw-brand-2, #1E54AC);
}

.nd-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.nd-btn-secondary {
    background: var(--rw-paper, #fff);
    border: 1px solid var(--rw-line, #E5E2DC);
    color: var(--rw-ink, #0F1115);
}

.nd-btn-secondary:hover {
    border-color: var(--rw-ink-3, #6B7280);
}

.nd-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: nd-spin 0.6s linear infinite;
}

@keyframes nd-spin {
    to { transform: rotate(360deg); }
}

.nd-price-breakdown {
    border: 1px solid var(--rw-line, #E5E2DC);
    border-radius: 14px;
    padding: 4px 16px;
    background: var(--rw-bg-2, #FBFAF7);
    display: flex;
    flex-direction: column;
    margin-top: -8px;
}

.nd-pb-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 9px 0;
    font-size: 13px;
    color: var(--rw-ink-3, #6B7280);
    border-bottom: 1px solid var(--rw-line-2, #EDEAE3);
}

.nd-pb-row:last-child {
    border-bottom: none;
}

.nd-pb-row--adj span:last-child {
    color: var(--rw-ink, #1A1A1A);
}

.nd-pb-row--discount span:last-child {
    color: #16a34a;
}

.nd-pb-divider {
    height: 1px;
    background: var(--rw-line, #E5E2DC);
    margin: 2px 0;
}

.nd-pb-total {
    font-size: 15px;
    font-weight: 600;
    color: var(--rw-ink, #1A1A1A);
    padding-top: 10px;
    border-bottom: none;
}
</style>
